import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import {
  ToastComponent,
  ToastData,
  ToastType,
} from '@app/toast/toast.component';

export interface ToastOptions {
  /** Riga in grassetto sopra al messaggio. */
  title?: string;
  /** Etichetta del pulsante di azione; sottoscrivere ref.onAction() per reagire. */
  actionLabel?: string;
  /** Sovrascrive la durata di default del tipo. 0 = resta aperto finché non si chiude. */
  duration?: number;
}

/**
 * Durate per tipo: gli errori restano più a lungo perché richiedono lettura,
 * i successi sono conferme che non devono intralciare.
 */
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  info: 3500,
  warning: 5000,
  error: 6000,
};

/**
 * Punto unico per le toast dell'app. Da preferire a MatSnackBar diretto:
 * centralizza stile, durate, posizione e accessibilità.
 *
 * @example
 * this.toast.success('Documento caricato');
 * this.toast.error('Salvataggio non riuscito', { title: 'Errore' });
 * this.toast.info('Bozza salvata', { actionLabel: 'Annulla' })
 *   .onAction().subscribe(() => this.undo());
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  constructor(private snackBar: MatSnackBar) {}

  success(message: string, options?: ToastOptions): MatSnackBarRef<ToastComponent> {
    return this.show('success', message, options);
  }

  error(message: string, options?: ToastOptions): MatSnackBarRef<ToastComponent> {
    return this.show('error', message, options);
  }

  warning(message: string, options?: ToastOptions): MatSnackBarRef<ToastComponent> {
    return this.show('warning', message, options);
  }

  info(message: string, options?: ToastOptions): MatSnackBarRef<ToastComponent> {
    return this.show('info', message, options);
  }

  /** Chiude il toast attualmente visibile, se presente. */
  dismiss(): void {
    this.snackBar.dismiss();
  }

  private show(
    type: ToastType,
    message: string,
    options: ToastOptions = {},
  ): MatSnackBarRef<ToastComponent> {
    const duration = options.duration ?? DEFAULT_DURATIONS[type];

    const data: ToastData = {
      message,
      title: options.title,
      type,
      actionLabel: options.actionLabel,
      duration,
    };

    return this.snackBar.openFromComponent(ToastComponent, {
      data,
      duration,
      // La panelClass neutralizza la superficie MDC (vedi styles.scss), così il
      // componente disegna interamente il proprio contenitore.
      panelClass: ['izy-toast-panel'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
      // Gli errori interrompono gli screen reader, il resto attende una pausa.
      politeness: type === 'error' ? 'assertive' : 'polite',
    });
  }
}
